import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Chip, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExpandMore, HelpOutline, Sync } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CategorySubscriptionModel, PodConfigurationModel, podClient } from '../../../app/services/api/podClient';
import { client, Item } from '../../../app/services/api/orderManagementClient';
import { ConfigurationLevel } from '../enums/ConfigurationLevel';
import CustomSwitch from '../../../components/shared/CustomSwitch';
import useLogError from '../../../hooks/useLogError';
import { Section } from '../../../components/ui/Section';
import { getInventorySku } from '../../../utils';

const LAYOUT = {
  panelInnerPadding: '24px',
  panelGap: '24px',
  rightEdgePadding: '24px',
  orderTypeMinHeight: '52px',
  categoryMinHeight: '52px',
  itemMinHeight: '52px',
  chipColumnWidth: '64px',
};

/** Item with order-type and category hierarchy fields returned by GetFranchiseCatalogue. */
interface CatalogueItem extends Item {
  orderType?: number;
  orderTypeName?: string;
}

type ToggleState = 'none' | 'some' | 'all';

interface CategoryNode {
  categoryId: number;
  categoryName: string;
  items: Item[];
  enabledCount: number;
  totalCount: number;
  state: ToggleState;
}

interface OrderTypeNode {
  orderTypeId: number;
  orderTypeName: string;
  categories: CategoryNode[];
  enabledCount: number;
  totalCount: number;
  state: ToggleState;
}

interface BulkCategoryTogglesProps {
  franchiseName: string;
  existingConfigurations: PodConfigurationModel[];
  /** All store-level item configs (for cascade). */
  allStoreItemConfigs: PodConfigurationModel[];
  /** Stores that are enabled for predictive ordering (for cascade). */
  enabledStores: Array<{ storeNumber: string; storeName: string; starbucksStoreNumber: string }>;
  onBulkComplete: () => void;
}

/**
 * Batch item configuration editor. Loads the franchise catalogue
 * (OrderType -> Category -> Items) and lets the user toggle items on/off
 * without saving immediately. A "Save" button commits all pending changes.
 *
 * Toggle logic:
 * - Toggle order type -> enable/disable ALL items under it
 * - Toggle category -> enable/disable ALL items in that category
 * - Toggle item -> enable/disable only that item
 *
 * NOTE: Bulk disable does NOT clear existing min/max stock values;
 * it only sets enabled=false. Values are preserved for re-enabling later.
 */
const BulkCategoryToggles = (props: BulkCategoryTogglesProps) => {
  const { franchiseName, existingConfigurations, allStoreItemConfigs, enabledStores, onBulkComplete } = props;
  const { t } = useTranslation('managePodConfigurations');
  const { logError } = useLogError();
  const theme = useTheme();

  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const columnCount = isLg ? 3 : isMd ? 2 : 1;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [catalogueItems, setCatalogueItems] = useState<CatalogueItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<CategorySubscriptionModel[]>([]);

  /**
   * Pending changes: tracks local overrides before saving.
   * Key = itemNumber, Value = desired enabled state.
   * Only items that DIFFER from their server state appear here.
   */
  const [pendingChanges, setPendingChanges] = useState<Map<string, boolean>>(new Map());
  const [manuallyChangedItems, setManuallyChangedItems] = useState<Set<string>>(new Set());
  /**
   * Pending AI-ordering subscription changes. Full order-type/category toggles opt scopes in or
   * out; narrower opt-outs also clear broader subscriptions so reconciliation cannot re-enable an
   * item the user excluded.
   * Key = `${orderTypeId}:${categoryId ?? 'all'}`.
   */
  const [pendingSubscriptionChanges, setPendingSubscriptionChanges] = useState<Map<string, CategorySubscriptionModel>>(new Map());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const getSubscriptionKey = (orderTypeId: number, categoryId: number | null): string =>
    `${orderTypeId}:${categoryId ?? 'all'}`;

  const persistedSubscriptionsByKey = useMemo(
    () => new Map(subscriptions.map(subscription => [
      getSubscriptionKey(subscription.orderTypeId, subscription.categoryId),
      subscription,
    ])),
    [subscriptions],
  );

  const effectiveSubscriptionsByKey = useMemo(() => {
    const effective = new Map(persistedSubscriptionsByKey);
    pendingSubscriptionChanges.forEach((subscription, key) => effective.set(key, subscription));
    return effective;
  }, [persistedSubscriptionsByKey, pendingSubscriptionChanges]);

  const isOrderTypeSubscribed = (orderTypeId: number): boolean =>
    effectiveSubscriptionsByKey.get(getSubscriptionKey(orderTypeId, null))?.subscribed === true;

  const isCategorySubscribed = (orderTypeId: number, categoryId: number): boolean =>
    isOrderTypeSubscribed(orderTypeId) ||
    effectiveSubscriptionsByKey.get(getSubscriptionKey(orderTypeId, categoryId))?.subscribed === true;

  const setPendingSubscription = (
    changes: Map<string, CategorySubscriptionModel>,
    orderTypeId: number,
    categoryId: number | null,
    subscribed: boolean,
  ) => {
    const key = getSubscriptionKey(orderTypeId, categoryId);
    const persisted = persistedSubscriptionsByKey.get(key);
    if ((persisted?.subscribed ?? false) === subscribed) {
      changes.delete(key);
      return;
    }

    changes.set(key, {
      ...persisted,
      franchiseName,
      orderTypeId,
      categoryId,
      subscribed,
    });
  };

  /** Resolves an item's POD item number using the shared inventory-number rule. */
  const getItemNumber = (item: Item): string => getInventorySku(item);

  /** Set of item numbers that are currently enabled on the server. */
  const serverEnabledItems = useMemo(() => new Set(
    existingConfigurations
      .filter(c => c.itemNumber && c.enabled)
      .map(c => c.itemNumber),
  ), [existingConfigurations]);

  const catalogueItemsByNumber = useMemo(() => {
    const result = new Map<string, CatalogueItem[]>();
    catalogueItems.forEach(item => {
      const itemNumber = getItemNumber(item);
      if (!itemNumber) return;
      result.set(itemNumber, [...(result.get(itemNumber) ?? []), item]);
    });
    return result;
  }, [catalogueItems]);

  const storeConfigurationsByItemNumber = useMemo(() => {
    const result = new Map<string, PodConfigurationModel[]>();
    allStoreItemConfigs
      .filter(configuration => configuration.franchiseName === franchiseName)
      .forEach(configuration => {
        result.set(
          configuration.itemNumber,
          [...(result.get(configuration.itemNumber) ?? []), configuration],
        );
      });
    return result;
  }, [allStoreItemConfigs, franchiseName]);

  /** Returns the effective enabled state for an item (server state + pending override). */
  const isItemEnabled = useCallback((itemNumber: string): boolean => {
    if (pendingChanges.has(itemNumber)) {
      return pendingChanges.get(itemNumber)!;
    }
    return serverEnabledItems.has(itemNumber);
  }, [pendingChanges, serverEnabledItems]);

  /** Computes toggle state from counts. */
  const computeState = (enabled: number, total: number): ToggleState => {
    if (total === 0) return 'none';
    if (enabled === 0) return 'none';
    if (enabled >= total) return 'all';
    return 'some';
  };

  /** Builds the hierarchy from catalogue items using the effective enabled state. */
  const hierarchy = useMemo((): OrderTypeNode[] => {
    if (catalogueItems.length === 0) return [];

    const otMap = new Map<number, { name: string; catMap: Map<number, { name: string; items: Item[] }> }>();

    for (const item of catalogueItems) {
      const otId = (item as CatalogueItem).orderType ?? 0;
      const otName = (item as CatalogueItem).orderTypeName ?? t('uncategorized');
      const catId = item.categoryId ?? 0;
      const catName = item.category ?? t('uncategorized');

      if (!otMap.has(otId)) {
        otMap.set(otId, { name: otName, catMap: new Map() });
      }
      const otEntry = otMap.get(otId)!;
      if (!otEntry.catMap.has(catId)) {
        otEntry.catMap.set(catId, { name: catName, items: [] });
      }
      const categoryItems = otEntry.catMap.get(catId)!.items;
      if (!categoryItems.some(existing => getItemNumber(existing) === getItemNumber(item))) {
        categoryItems.push(item);
      }
    }

    const nodes: OrderTypeNode[] = [];
    for (const [otId, otEntry] of Array.from(otMap.entries())) {
      const categoryNodes: CategoryNode[] = [];
      for (const [catId, catEntry] of Array.from(otEntry.catMap.entries())) {
        const enabledCount = catEntry.items.filter((i: Item) => isItemEnabled(getItemNumber(i))).length;
        categoryNodes.push({
          categoryId: catId,
          categoryName: catEntry.name,
          items: catEntry.items,
          enabledCount,
          totalCount: catEntry.items.length,
          state: computeState(enabledCount, catEntry.items.length),
        });
      }
      categoryNodes.sort((a, b) => a.categoryName.localeCompare(b.categoryName));

      const totalItems = categoryNodes.reduce((sum, c) => sum + c.totalCount, 0);
      const totalEnabled = categoryNodes.reduce((sum, c) => sum + c.enabledCount, 0);

      nodes.push({
        orderTypeId: otId,
        orderTypeName: otEntry.name,
        categories: categoryNodes,
        enabledCount: totalEnabled,
        totalCount: totalItems,
        state: computeState(totalEnabled, totalItems),
      });
    }
    nodes.sort((a, b) => a.orderTypeName.localeCompare(b.orderTypeName));
    return nodes;
  }, [catalogueItems, isItemEnabled]);

  const masonryColumns = useMemo(() => {
    const cols: OrderTypeNode[][] = Array.from({ length: columnCount }, () => []);
    hierarchy.forEach((node, index) => {
      cols[index % columnCount].push(node);
    });
    return cols;
  }, [hierarchy, columnCount]);

  /** Loads the franchise catalogue and persisted subscription state from the backend. */
  const loadCatalogue = useCallback(async () => {
    if (!franchiseName) return;
    setIsLoading(true);

    try {
      const [result, savedSubscriptions] = await Promise.all([
        client.getFranchiseCatalogue(franchiseName, -1, ''),
        podClient.getCategorySubscriptions(franchiseName),
      ]);
      if (result?.data) {
        setCatalogueItems(result.data as CatalogueItem[]);
      }
      setSubscriptions(savedSubscriptions);
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  }, [franchiseName]);

  useEffect(() => {
    loadCatalogue();
  }, [franchiseName]);

  // Reset pending changes when server state changes (e.g. after save)
  useEffect(() => {
    setPendingChanges(new Map());
    setManuallyChangedItems(new Set());
    setPendingSubscriptionChanges(new Map());
  }, [existingConfigurations]);

  // --- Toggle handlers (accumulate locally, no API calls) ---

  /** Applies a batch toggle to a list of items. */
  const applyToggle = (items: Item[], enabled: boolean, manual = false) => {
    setManuallyChangedItems(previous => {
      const next = new Set(previous);
      items.forEach(item => {
        const itemNumber = getItemNumber(item);
        if (manual && serverEnabledItems.has(itemNumber) !== enabled) {
          next.add(itemNumber);
        } else {
          next.delete(itemNumber);
        }
      });
      return next;
    });
    setPendingChanges(prev => {
      const next = new Map(prev);
      for (const item of items) {
        const itemNumber = getItemNumber(item);
        const serverState = serverEnabledItems.has(itemNumber);
        if (serverState === enabled) {
          // No change from server state — remove pending override
          next.delete(itemNumber);
        } else {
          next.set(itemNumber, enabled);
        }
      }
      return next;
    });
  };

  /** Toggle all items under an order type. Records a whole-order-type AI ordering subscription. */
  const handleOrderTypeToggle = (otNode: OrderTypeNode) => {
    const allItems = otNode.categories.flatMap(c => c.items);
    const newEnabled = otNode.state !== 'all';
    applyToggle(allItems, newEnabled);

    setPendingSubscriptionChanges(prev => {
      const next = new Map(prev);
      setPendingSubscription(next, otNode.orderTypeId, null, newEnabled);
      const categoryIds = new Set([
        ...otNode.categories.map(category => category.categoryId),
        ...Array.from(effectiveSubscriptionsByKey.values())
          .filter(subscription =>
            subscription.orderTypeId === otNode.orderTypeId &&
            subscription.categoryId !== null)
          .map(subscription => subscription.categoryId!),
      ]);
      categoryIds.forEach(categoryId =>
        setPendingSubscription(next, otNode.orderTypeId, categoryId, false),
      );
      return next;
    });
  };

  /** Toggle all items in a category. Records a single-category AI ordering subscription. */
  const handleCategoryToggle = (catNode: CategoryNode, orderTypeId: number) => {
    const newEnabled = catNode.state !== 'all';
    applyToggle(catNode.items, newEnabled);

    setPendingSubscriptionChanges(prev => {
      const next = new Map(prev);
      setPendingSubscription(next, orderTypeId, catNode.categoryId, newEnabled);
      if (!newEnabled) {
        setPendingSubscription(next, orderTypeId, null, false);
      }
      return next;
    });
  };

  /** Toggle a single item. */
  const handleItemToggle = (item: Item) => {
    const itemNumber = getItemNumber(item);
    const currentlyEnabled = isItemEnabled(itemNumber);
    const enabled = !currentlyEnabled;
    applyToggle([item], enabled, true);
  };

  /** Discard all pending changes. */
  const handleCancel = () => {
    setPendingChanges(new Map());
    setManuallyChangedItems(new Set());
    setPendingSubscriptionChanges(new Map());
  };

  /** Number of pending item changes (subscription-only changes are folded into this for gating). */
  const pendingCount = pendingChanges.size;
  const hasPendingChanges = pendingChanges.size > 0 || pendingSubscriptionChanges.size > 0;

  // --- Save logic ---

  /** Shows the confirmation dialog before committing. */
  const handleSaveClick = () => {
    if (!hasPendingChanges) return;
    setConfirmOpen(true);
  };

  /** Commits all pending changes to the API with cascade to stores. */
  const handleSave = async () => {
    setConfirmOpen(false);
    if (!hasPendingChanges) return;
    setIsSaving(true);

    try {
      const subscriptionByKey = new Map<string, CategorySubscriptionModel>();
      subscriptions.forEach(subscription =>
        subscriptionByKey.set(getSubscriptionKey(subscription.orderTypeId, subscription.categoryId), subscription),
      );
      for (const subscription of Array.from(pendingSubscriptionChanges.values())) {
        subscriptionByKey.set(getSubscriptionKey(subscription.orderTypeId, subscription.categoryId), subscription);
      }

      const resolveSubscription = (itemNumber: string): CategorySubscriptionModel | null => {
        if (manuallyChangedItems.has(itemNumber)) return null;

        const candidates = (catalogueItemsByNumber.get(itemNumber) ?? [])
          .flatMap(item => [
            subscriptionByKey.get(getSubscriptionKey(item.orderType ?? 0, null)),
            subscriptionByKey.get(getSubscriptionKey(item.orderType ?? 0, item.categoryId ?? 0)),
          ])
          .filter((subscription): subscription is CategorySubscriptionModel =>
            subscription?.subscribed === true)
          .sort((left, right) =>
            Number(left.categoryId !== null) - Number(right.categoryId !== null) ||
            left.orderTypeId - right.orderTypeId ||
            (left.categoryId ?? -1) - (right.categoryId ?? -1) ||
            (left.id ?? 0) - (right.id ?? 0));

        return candidates[0] ?? null;
      };

      const withSubscription = (
        configuration: PodConfigurationModel,
        subscription: CategorySubscriptionModel | null,
      ): PodConfigurationModel => ({
        ...configuration,
        subscriptionId: subscription?.id ?? null,
        subscriptionOrderTypeId: subscription?.orderTypeId ?? null,
        subscriptionCategoryId: subscription?.categoryId ?? null,
      });

      const toCreate: PodConfigurationModel[] = [];
      const toUpdate: PodConfigurationModel[] = [];

      for (const [itemNumber, enabled] of Array.from(pendingChanges.entries())) {
        const existing = existingConfigurations.find(c => c.itemNumber === itemNumber);
        const item = catalogueItemsByNumber.get(itemNumber)?.[0];
        const subscription = enabled ? resolveSubscription(itemNumber) : null;
        const subscriptionId = subscription?.id ?? null;

        // Franchise-level config
        if (existing) {
          if (existing.enabled !== enabled ||
              existing.subscriptionId !== subscriptionId ||
              (subscription !== null && subscription.id === undefined)) {
            toUpdate.push(withSubscription({ ...existing, enabled }, subscription));
          }
        } else if (enabled) {
          toCreate.push({
            id: 0,
            franchiseName,
            storeNumber: '',
            storeName: '',
            starbucksStoreNumber: '',
            itemNumber,
            itemDescription: item?.name ?? '',
            configurationLevel: ConfigurationLevel.Franchise,
            minStockOnHandQuantity: null,
            maxStockOnHandQuantity: null,
            maxOrderQuantity: null,
            safetyStock: null,
            isParEnabled: false,
            enabled: true,
            franchiseStores: [],
            subscriptionId,
            subscriptionOrderTypeId: subscription?.orderTypeId ?? null,
            subscriptionCategoryId: subscription?.categoryId ?? null,
          });
        }

        // CASCADE to enabled stores
        if (enabled) {
          const storesWithConfig = new Set(
            (storeConfigurationsByItemNumber.get(itemNumber) ?? [])
              .map(c => c.storeNumber),
          );
          for (const store of enabledStores) {
            if (!storesWithConfig.has(store.storeNumber)) {
              toCreate.push({
                id: 0,
                franchiseName,
                storeNumber: store.storeNumber,
                storeName: store.storeName,
                starbucksStoreNumber: store.starbucksStoreNumber,
                itemNumber,
                itemDescription: item?.name ?? '',
                configurationLevel: ConfigurationLevel.Store,
                minStockOnHandQuantity: null,
                maxStockOnHandQuantity: null,
                maxOrderQuantity: null,
                safetyStock: null,
                isParEnabled: false,
                enabled: true,
                franchiseStores: [],
                subscriptionId,
                subscriptionOrderTypeId: subscription?.orderTypeId ?? null,
                subscriptionCategoryId: subscription?.categoryId ?? null,
              });
            }
          }
          // Re-enable disabled store configs
          (storeConfigurationsByItemNumber.get(itemNumber) ?? [])
            .filter(c => !c.enabled &&
              enabledStores.some(s => s.storeNumber === c.storeNumber))
            .forEach(config => toUpdate.push(withSubscription({ ...config, enabled: true }, subscription)));
        } else {
          // Disable all enabled store configs for this item
          (storeConfigurationsByItemNumber.get(itemNumber) ?? [])
            .filter(c => c.enabled || c.subscriptionId !== null)
            .forEach(config => toUpdate.push(withSubscription({ ...config, enabled: false }, null)));
        }
      }

      // Deduplicate by config key
      const createMap = new Map<string, PodConfigurationModel>();
      const updateMap = new Map<string, PodConfigurationModel>();
      for (const c of toCreate) createMap.set(`${c.franchiseName}-${c.storeNumber}-${c.itemNumber}`, c);
      for (const u of toUpdate) updateMap.set(`${u.franchiseName}-${u.storeNumber}-${u.itemNumber}`, u);

      // Align every existing enabled row with its final active coverage. Clearing an obsolete ID is
      // essential when a narrow opt-out deactivates a broader subscription: unrelated enabled rows
      // must become manually managed rather than being disabled by reconciliation overnight.
      for (const itemNumber of Array.from(catalogueItemsByNumber.keys())) {
        const subscription = resolveSubscription(itemNumber);
        const subscriptionId = subscription?.id ?? null;
        if (!isItemEnabled(itemNumber)) continue;

        const coveredConfigurations = [
          ...existingConfigurations.filter(config => config.itemNumber === itemNumber),
          ...(storeConfigurationsByItemNumber.get(itemNumber) ?? []).filter(config =>
            enabledStores.some(store => store.storeNumber === config.storeNumber),
          ),
        ];

        for (const config of coveredConfigurations) {
          const key = `${config.franchiseName}-${config.storeNumber}-${config.itemNumber}`;
          const pending = updateMap.get(key) ?? config;
          if (pending.subscriptionId !== subscriptionId ||
              (subscription !== null && subscription.id === undefined)) {
            updateMap.set(key, withSubscription(pending, subscription));
          }
        }
      }

      if (createMap.size > 0 || updateMap.size > 0 || pendingSubscriptionChanges.size > 0) {
        const savedSubscriptions = await podClient.saveBulkConfiguration({
          franchiseName,
          subscriptionChanges: Array.from(pendingSubscriptionChanges.values()),
          configurationsToCreate: Array.from(createMap.values()),
          configurationsToUpdate: Array.from(updateMap.values()),
        });
        savedSubscriptions.forEach(subscription =>
          subscriptionByKey.set(getSubscriptionKey(subscription.orderTypeId, subscription.categoryId), subscription),
        );
        setSubscriptions(Array.from(subscriptionByKey.values()));
        setPendingSubscriptionChanges(new Map());
        onBulkComplete();
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsSaving(false);
    }
  };

  // --- Render helpers ---

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="textSM"
          color="text.secondary">
          {t('loadingBulkToggles')}
        </Typography>
      </Box>
    );
  }

  if (hierarchy.length === 0) {
    return null;
  }

  /** Renders a single item row. */
  const renderItemRow = (item: Item) => {
    const itemNumber = getItemNumber(item);
    const enabled = isItemEnabled(itemNumber);
    const hasChange = pendingChanges.has(itemNumber);

    // Store-enabled counts for this item
    const storeConfigsForItem = storeConfigurationsByItemNumber.get(itemNumber) ?? [];
    const storeEnabledCount = storeConfigsForItem.filter(c => c.enabled).length;
    const totalEnabledStores = enabledStores.length;
    const storeChipColor: 'success' | 'warning' | 'default' =
      storeEnabledCount >= totalEnabledStores && totalEnabledStores > 0 ? 'success'
        : storeEnabledCount > 0 ? 'warning'
          : 'default';

    return (
      <Box
        key={`item-${itemNumber}`}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          minHeight: LAYOUT.itemMinHeight, py: 2, pl: '32px', pr: LAYOUT.rightEdgePadding,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:last-child': { borderBottom: 'none' },
          ...(hasChange ? { backgroundColor: alpha(theme.palette.primary.main, 0.04) } : {}),
        }}
      >
        <CustomSwitch
          checked={enabled}
          name={`item-${itemNumber}`}
          disabled={isSaving}
          onChange={() => handleItemToggle(item)}
          inputProps={{ 'aria-label': item.name ?? itemNumber } as React.InputHTMLAttributes<HTMLInputElement>}
        />
        <Typography variant="textSM"
          color="text.secondary"
          sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 72 }}>
          {itemNumber}
        </Typography>
        <Typography variant="textSM"
          sx={{ flex: 1 }}>
          {item.name ?? itemNumber}
        </Typography>
        {/* Store count chip — aligned with category/order-type chips */}
        <Box sx={{ minWidth: LAYOUT.chipColumnWidth, display: 'flex', justifyContent: 'flex-end' }}>
          {totalEnabledStores > 0 && (
            <Tooltip
              title={t('storesEnabledCount', { enabled: storeEnabledCount, total: totalEnabledStores })}
              arrow
              placement="top"
            >
              <Chip
                size="small"
                label={`${storeEnabledCount} / ${totalEnabledStores}`}
                color={storeChipColor}
                variant="outlined"
              />
            </Tooltip>
          )}
        </Box>
        {/* 24px spacer matching the icon slot in category/order-type rows */}
        <Box sx={{ width: '24px', flexShrink: 0 }} />
      </Box>
    );
  };

  /** Renders a category as an accordion with items fanning out below. */
  const renderCategoryAccordion = (catNode: CategoryNode, orderTypeId: number) => {
    const categoryKey = `${orderTypeId}:${catNode.categoryId}`;
    const expanded = expandedCategories.has(categoryKey);
    return (
      <Accordion
        key={`cat-${catNode.categoryId}`}
        expanded={expanded}
        onChange={(_, nextExpanded) => setExpandedCategories(previous => {
          const next = new Set(previous);
          if (nextExpanded) next.add(categoryKey);
          else next.delete(categoryKey);
          return next;
        })}
        TransitionProps={{ unmountOnExit: true }}
        disableGutters
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '0 !important',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={null}
          sx={{
            minHeight: `${LAYOUT.categoryMinHeight} !important`,
            py: 1.25, pl: '16px', pr: LAYOUT.rightEdgePadding,
            '&.Mui-expanded': { minHeight: `${LAYOUT.categoryMinHeight} !important` },
            '& .MuiAccordionSummary-content': { my: '0 !important' },
            '& .MuiAccordionSummary-content.Mui-expanded': { my: '0 !important' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <CustomSwitch
              checked={catNode.state === 'all'}
              indeterminate={catNode.state === 'some'}
              name={`cat-${catNode.categoryId}`}
              disabled={isSaving}
              onChange={() => handleCategoryToggle(catNode, orderTypeId)}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              inputProps={{ 'aria-label': catNode.categoryName } as React.InputHTMLAttributes<HTMLInputElement>}
            />
            <Typography variant="textSM"
              sx={{ flex: 1, fontWeight: 500 }}>
              {catNode.categoryName}
            </Typography>
            {/* Trailing cluster: [auto-add badge][chip][24px icon slot] */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 1 }}>
              {isCategorySubscribed(orderTypeId, catNode.categoryId) && (
                <Tooltip title={t('autoAddEnabledTooltip')}
                  arrow>
                  <Sync sx={{ fontSize: 16, color: 'success.main' }}
                    aria-label={t('autoAddEnabled')} />
                </Tooltip>
              )}
              <Box sx={{ minWidth: LAYOUT.chipColumnWidth, display: 'flex', justifyContent: 'flex-end' }}>
                <Chip
                  size="small"
                  label={`${catNode.enabledCount} / ${catNode.totalCount}`}
                  color={catNode.state === 'all' ? 'success' : catNode.state === 'some' ? 'warning' : 'default'}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ width: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ExpandMore sx={{ fontSize: 20, color: 'action.active', transition: 'transform 0.2s', '.Mui-expanded &': { transform: 'rotate(180deg)' } }} />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: '0 !important', borderTop: 'none' }}>
          {expanded && catNode.items.map(renderItemRow)}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      {/* Action bar: description + save/cancel */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, mb: 5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="textSM"
            color="text.secondary">
            {t('bulkToggleDescription')}
          </Typography>
          <Tooltip title={t('bulkToggleTooltip')}
            arrow
            placement="top">
            <HelpOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {hasPendingChanges && (
            <Button
              variant="secondary"
              onClick={handleCancel}
              sx={{
                color: 'text.secondary',
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {t('cancelAll')}
            </Button>
          )}
          <Button
            variant="primary"
            size="lg"
            onClick={handleSaveClick}
            disabled={!hasPendingChanges || isSaving}
          >
            {t('saveAll', { count: pendingCount })}
          </Button>
        </Box>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {isSaving && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
          }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {/* JS Partitioned Masonry layout for order-type groups */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: LAYOUT.panelGap,
            alignItems: 'flex-start',
          }}
        >
          {masonryColumns.map((col, colIdx) => (
            <Box key={colIdx}
              sx={{ display: 'flex', flexDirection: 'column', gap: LAYOUT.panelGap, flex: 1, minWidth: 0, width: '100%' }}>
              {col.map((otNode) => (
                <Section
                  key={otNode.orderTypeId}
                  style={{
                    overflow: 'hidden',
                    p: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      minHeight: LAYOUT.orderTypeMinHeight, pl: '12px', pr: LAYOUT.rightEdgePadding, py: 1.25,
                      backgroundColor: alpha(theme.palette.common.black, 0.045),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <CustomSwitch
                      checked={otNode.state === 'all'}
                      indeterminate={otNode.state === 'some'}
                      name={`ot-${otNode.orderTypeId}`}
                      disabled={isSaving}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleOrderTypeToggle(otNode);
                      }}
                      inputProps={{ 'aria-label': otNode.orderTypeName } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                    <Typography variant="textMD"
                      sx={{ fontWeight: 600, flex: 1 }}>
                      {otNode.orderTypeName}
                    </Typography>
                    {/* Trailing cluster: [auto-add badge][chip][24px icon slot] */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 1 }}>
                      {isOrderTypeSubscribed(otNode.orderTypeId) && (
                        <Tooltip title={t('autoAddEnabledTooltip')}
                          arrow>
                          <Sync sx={{ fontSize: 16, color: 'success.main' }}
                            aria-label={t('autoAddEnabled')} />
                        </Tooltip>
                      )}
                      <Box sx={{ minWidth: LAYOUT.chipColumnWidth, display: 'flex', justifyContent: 'flex-end' }}>
                        <Chip
                          size="small"
                          label={`${otNode.enabledCount} / ${otNode.totalCount}`}
                          color={otNode.state === 'all' ? 'success' : otNode.state === 'some' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ width: '24px', flexShrink: 0 }} />
                    </Box>
                  </Box>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    {otNode.categories.map(cat => renderCategoryAccordion(cat, otNode.orderTypeId))}
                  </Box>
                </Section>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="bulk-save-confirm-title"
        aria-describedby="bulk-save-confirm-description"
      >
        <DialogTitle id="bulk-save-confirm-title">
          {t('confirmBulkSaveTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-save-confirm-description">
            {t('confirmBulkSave', { count: pendingCount })}
          </DialogContentText>
          {pendingSubscriptionChanges.size > 0 && (
            <DialogContentText sx={{ mt: 2 }}>
              {t('confirmBulkSaveAutoAddNote')}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}
            variant="secondary">
            {t('cancel')}
          </Button>
          <Button onClick={handleSave}
            variant="primary">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkCategoryToggles;
